import React, { useCallback, useEffect, useState } from 'react';
import {
  Pipe,
  Project,
  projectsApi,
  syncApi,
  SyncSessionSummaryResponse,
} from '../../api';
import { useToast } from '../../context/ToastContext';
import { Link, useParams } from '../../router/Router';
import { Badge, formatDateTime, formatRelativeTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import { IconSync, IconTrash } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';
import {
  useDebouncedValue,
  usePaginatedList,
  useServerPaginatedQuery,
} from '../../hooks/usePagination';

export function AdminProjectsPage() {
  const { projectId } = useParams<{ projectId?: string }>();
  const { pushToast } = useToast();

  // Projects Data Table State (`/ms-project/data-table`, 1-based page on backend)
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  const trimmedSearch = debouncedSearch.trim() || undefined;
  const projectsQuery = useServerPaginatedQuery<Project>({
    queryKey: `admin-projects:${trimmedSearch ?? ''}`,
    defaultPageSize: 10,
    enabled: !projectId,
    fetchPage: async (p, size) => {
      const res = await projectsApi.getProjectsDataTable(p + 1, size, undefined, trimmedSearch);
      const data = Array.isArray(res.data) ? res.data : [];
      const pages = Math.max(0, res.pages ?? (data.length > 0 ? 1 : 0));
      const rawTotal = (res as { total?: number }).total;
      const total =
        typeof rawTotal === 'number'
          ? rawTotal
          : pages === 0
            ? 0
            : p + 1 >= pages
              ? p * size + data.length
              : pages * size;
      return { data, total };
    },
  });
  const isSearchingProjects =
    projectsQuery.loading || searchQuery.trim() !== debouncedSearch.trim();

  // Project Pipes Inspector State (`/admin/projects/:projectId/pipes`)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [pipes, setPipes] = useState<Pipe[]>([]);
  const [loadingPipes, setLoadingPipes] = useState<boolean>(false);
  const pipesPagination = usePaginatedList(pipes, 10);

  // Pipe Sync Sessions Inspector Modal
  const [inspectingPipe, setInspectingPipe] = useState<Pipe | null>(null);
  const [pipeSessions, setPipeSessions] = useState<SyncSessionSummaryResponse[]>([]);
  const [loadingSessions, setLoadingSessions] = useState<boolean>(false);
  const sessionsPagination = usePaginatedList(pipeSessions, 10);

  useEffect(() => {
    if (projectsQuery.error) {
      pushToast({
        tone: 'error',
        title: 'Failed to load projects explorer',
        description: projectsQuery.error,
      });
    }
  }, [projectsQuery.error, pushToast]);

  const loadProjectPipes = useCallback(
    async (targetProjectId: string) => {
      setLoadingPipes(true);
      try {
        const [proj, pipeList] = await Promise.all([
          projectsApi.getProject(targetProjectId).catch(() => null),
          projectsApi.listProjectPipes(targetProjectId).catch(() => []),
        ]);
        setSelectedProject(proj);
        setPipes(Array.isArray(pipeList) ? pipeList : []);
      } finally {
        setLoadingPipes(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (projectId) {
      void loadProjectPipes(projectId);
    }
  }, [projectId, loadProjectPipes]);

  const handleReleasePipeLock = async (pipe: Pipe) => {
    try {
      await syncApi.releasePipeLock(pipe.id);
      if (projectId) {
        await loadProjectPipes(projectId);
      }
      pushToast({
        tone: 'success',
        title: 'Pipe synchronization lock force-released',
        description: `Released lock for pipe ${pipe.name} (${pipe.id}).`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to release pipe lock',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleDeletePipe = async (pipe: Pipe) => {
    try {
      await projectsApi.deletePipe(pipe.id);
      setPipes((prev) => prev.filter((p) => p.id !== pipe.id));
      pushToast({
        tone: 'info',
        title: 'Pipe deleted',
        description: `${pipe.name} removed.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not delete pipe',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const handleDeleteProject = async (proj: Project) => {
    try {
      await projectsApi.deleteProject(proj.id);
      await projectsQuery.reload();
      pushToast({
        tone: 'info',
        title: 'Project deleted',
        description: `${proj.name} removed.`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not delete project',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    }
  };

  const openInspectPipeSessions = async (pipe: Pipe) => {
    setInspectingPipe(pipe);
    setLoadingSessions(true);
    try {
      const list = await syncApi.listPipeSyncSessions(pipe.id, 100);
      setPipeSessions(Array.isArray(list) ? list : []);
    } catch {
      setPipeSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  if (projectId) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Link to="/admin/projects" className="hover:text-indigo-600 font-medium">
                ← All Projects Explorer
              </Link>
              <span>/</span>
              <span className="font-mono">{projectId}</span>
            </div>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">
              {selectedProject?.name || 'Project Pipes Inspector'}
            </h1>
            {selectedProject && (
              <p className="text-xs text-slate-500 font-mono">
                Procore Project #{selectedProject.procore_project_id} · Company{' '}
                {selectedProject.company_id}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => loadProjectPipes(projectId)}
            className="inline-flex items-center gap-1.5 self-start rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <IconSync className="w-4 h-4" />
            <span>Refresh Pipes</span>
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
          <div className="table-scroll-container">
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase font-semibold">
                <tr>
                  <th className="px-4 py-3">Pipe Name &amp; ID</th>
                  <th className="px-4 py-3">Type / Target</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Lock State</th>
                  <th className="px-4 py-3">Last Sync</th>
                  <th className="px-4 py-3 text-right">Admin Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loadingPipes ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      Loading project pipes…
                    </td>
                  </tr>
                ) : pipesPagination.pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      No pipes configured for this project.
                    </td>
                  </tr>
                ) : (
                  pipesPagination.pageItems.map((pipe) => (
                    <tr key={pipe.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{pipe.name}</div>
                        <div className="font-mono text-[11px] text-slate-400">{pipe.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="purple">{pipe.pipe_type}</Badge>
                          <Badge variant="info">{pipe.connected_to || 'smartsheet'}</Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={pipe.status === 'connected' ? 'success' : 'warning'}
                          dot
                        >
                          {pipe.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {pipe.is_locked ? (
                          <Badge variant="danger" dot>
                            LOCKED
                          </Badge>
                        ) : (
                          <Badge variant="neutral">Unlocked</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatRelativeTime(pipe.last_sync_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openInspectPipeSessions(pipe)}
                            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            Sessions
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReleasePipeLock(pipe)}
                            className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                          >
                            Force Unlock
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePipe(pipe)}
                            className="rounded-lg border border-rose-200 bg-rose-50 p-1 text-rose-700 hover:bg-rose-100"
                            title="Delete Pipe"
                          >
                            <IconTrash className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar pagination={pipesPagination} itemLabel="pipes" compact />
        </div>

        {/* Pipe Sync Sessions Modal */}
        <Dialog
          open={Boolean(inspectingPipe)}
          onClose={() => setInspectingPipe(null)}
          title={`Sync Sessions — ${inspectingPipe?.name || ''}`}
          subtitle={`Pipe UUID: ${inspectingPipe?.id || ''}`}
          maxWidthClass="max-w-3xl"
        >
          {loadingSessions ? (
            <p className="text-xs text-slate-500 py-6 text-center">Loading sessions…</p>
          ) : sessionsPagination.pageItems.length === 0 ? (
            <p className="text-xs text-slate-500 py-6 text-center">
              No sync sessions recorded for this pipe.
            </p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="table-scroll-container">
                <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 uppercase font-semibold">
                    <tr>
                      <th className="px-3 py-2">State</th>
                      <th className="px-3 py-2">Direction</th>
                      <th className="px-3 py-2">Trigger</th>
                      <th className="px-3 py-2">Metrics</th>
                      <th className="px-3 py-2">Started</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {sessionsPagination.pageItems.map((s) => (
                      <tr key={s.id}>
                        <td className="px-3 py-2">
                          <Badge variant={s.state === 'completed' ? 'success' : 'danger'}>
                            {s.state}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 font-mono">{s.direction}</td>
                        <td className="px-3 py-2">{s.trigger}</td>
                        <td className="px-3 py-2">
                          +{s.metrics?.items_created || 0} / ~{s.metrics?.items_updated || 0} / -
                          {s.metrics?.items_deleted || 0}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{formatDateTime(s.started_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar pagination={sessionsPagination} itemLabel="sessions" compact />
            </div>
          )}
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Cross-Tenant Projects &amp; Pipes Explorer
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Inspect customer projects across all companies, view pipes, and release stuck
            synchronization locks.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void projectsQuery.reload()}
          className="inline-flex items-center gap-1.5 self-start rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <IconSync className="w-4 h-4" />
          <span>Refresh Projects</span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            projectsQuery.setPage(0);
          }}
          placeholder="Search projects by name, Procore Project ID, Procore Company ID, or UUID…"
          className="w-full rounded-xl border border-slate-300 px-3.5 py-2 text-sm"
        />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
        <div className="table-scroll-container">
          <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-5 py-3.5">Project Name</th>
                <th className="px-4 py-3.5">Procore IDs</th>
                <th className="px-4 py-3.5">Company UUID</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5">Webhook</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isSearchingProjects ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    Loading cross-tenant projects…
                  </td>
                </tr>
              ) : projectsQuery.data.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No projects returned on this page.
                  </td>
                </tr>
              ) : (
                projectsQuery.data.map((proj) => (
                  <tr key={proj.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <Link
                        to={`/admin/projects/${proj.id}/pipes`}
                        className="text-sm font-semibold text-indigo-600 hover:underline"
                      >
                        {proj.name}
                      </Link>
                      <div className="font-mono text-[11px] text-slate-400">{proj.id}</div>
                    </td>
                    <td className="px-4 py-4 font-mono">
                      <div>Proj #{proj.procore_project_id}</div>
                      <div className="text-[11px] text-slate-400">
                        Comp #{proj.procore_company_id}
                      </div>
                    </td>
                    <td className="px-4 py-4 font-mono text-slate-600">
                      <Link
                        to={`/admin/companies/${proj.company_id}/users`}
                        className="hover:text-indigo-600 hover:underline"
                      >
                        {proj.company_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-4 py-4">
                      <Badge
                        variant={proj.status === 'connected' ? 'success' : 'neutral'}
                        dot
                      >
                        {proj.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <Badge
                        variant={proj.webhook?.status === 'active' ? 'purple' : 'neutral'}
                      >
                        {proj.webhook?.status || 'not_installed'}
                      </Badge>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Link
                          to={`/admin/projects/${proj.id}/pipes`}
                          className="rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                        >
                          Inspect Pipes →
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDeleteProject(proj)}
                          className="rounded-lg border border-rose-200 bg-rose-50 p-1.5 text-rose-700 hover:bg-rose-100"
                          title="Delete Project"
                        >
                          <IconTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar pagination={projectsQuery} itemLabel="projects" />
      </div>
    </div>
  );
}
